import { useState } from 'react';
import Checkbox from '@atlaskit/checkbox';
import Lozenge from '@atlaskit/lozenge';
import { useFlags } from '@atlaskit/flag';
import SharedButton from 'shared/components/buttons/button';
import DevToolsApiService from 'core/api/dev-tools/dev-tools-api.service';
import { IDeliveryRequestSeedResult } from 'core/api/dev-tools/dev-tools-api-interface';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';

/**
 * One button for a batch worth optimising.
 *
 * The requests are laid out on the backend (`buildDemoBatch`) so the solved run
 * shows the collect-and-deliver chain: two collected together where runs start,
 * then stops where one job comes off the van and the next goes on. Marked
 * Accept by default, so a demo goes straight from here to Queue → Commit →
 * Optimise.
 */

interface IProps {
  size: number;
}

/**
 * Always the v2 form, whatever the format dropdown below says: the legacy path
 * books a transport job straight from the email and makes no request, so there
 * would be nothing to optimise.
 */
const DemoBatchPanel = ({ size }: IProps) => {
  const flags = useFlags();
  const [markAccepted, setMarkAccepted] = useState(true);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<IDeliveryRequestSeedResult[]>();

  const send = async () => {
    setSending(true);
    setResults(undefined);
    try {
      const response = await DevToolsApiService.seedDemoBatch({ format: 'v2', markAccepted });
      setResults(response.results);
      const recorded = response.results.filter((result) => result.requestUid).length;
      const marked = response.results.filter((result) => result.markedAccept).length;
      // A 200 is not a request. Said as a failure rather than a success with a
      // zero in it, which is how six stray jobs went unnoticed the first time.
      if (recorded < response.results.length) {
        showErrorFlag(
          'Demo batch incomplete',
          `Only ${recorded} of ${response.results.length} became delivery requests.`,
          flags,
        );
        return;
      }
      showSuccessFlag(
        'Demo batch sent',
        markAccepted
          ? `${recorded} of ${response.results.length} recorded, ${marked} waiting in the Queue as Accept.`
          : `${recorded} of ${response.results.length} recorded, waiting in Review.`,
        flags,
      );
    } catch (error) {
      showErrorFlag('Demo batch failed', (error as Error).message, flags);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className='rounded border p-5' data-testid='demo-batch-panel'>
      <p className='body-02 font-semibold'>Demo batch</p>
      <p className='body-01 mt-1 text-gray-600'>
        {size} requests for tomorrow morning across the five sites, laid out so an optimised run collects and delivers
        along one chain. Then go to Delivery Requests → Queue → Commit → Optimise.
      </p>

      <div className='mt-3'>
        <Checkbox
          isChecked={markAccepted}
          onChange={(event) => setMarkAccepted(event.target.checked)}
          label='Put them straight in the Queue, marked Accept'
          testId='demo-mark-accepted'
        />
      </div>

      <div className='mt-4'>
        <SharedButton
          type='button'
          appearance='primary'
          label='Create demo batch'
          isLoading={sending}
          onClick={send}
          testId='create-demo-batch'
        />
      </div>

      {results && (
        <div className='mt-4'>
          {results.map((result) => (
            <div key={result.index} className='flex items-center justify-between border-t py-2'>
              <div>
                <p className='body-01 font-semibold'>{result.route}</p>
                <p className='body-01 text-gray-600'>
                  {result.requestType} · {result.requiredAt}
                </p>
              </div>
              <Lozenge appearance={result.markedAccept ? 'success' : result.requestUid ? 'inprogress' : 'removed'}>
                {result.markedAccept ? 'In the Queue' : result.requestStatus ?? `HTTP ${result.status ?? '—'}`}
              </Lozenge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DemoBatchPanel;
